import styled from '@emotion/styled';
import mime from 'mime-types';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import formsS3Uploader from './formsS3Uploader';
import Icon from '../../OC/oc-icon';
import dataTrackMap from '../../helpers/data_track_prop_map';
import { fileNameFromUrl } from '../../helpers/get_file_basename';
import getFileSize from '../../helpers/get_file_size';

const DropZone = styled.div`
  border: 2px dashed ${props => (props.isDragActive ? '#d40f7d' : '#bcbcbc')};
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  background-color: ${props => (props.isDragActive ? '#fef7fb' : '#f8f8f8')};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border-color: #d40f7d;
    background-color: #fef7fb;
  }

  &.error {
    border-color: #940420;
    background-color: #fff5f5;
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  color: ${props => (props.isDragActive ? '#d40f7d' : '#6d6d6d')};
  margin-bottom: 16px;
  transition: color 0.3s ease;
`;

const UploadTitle = styled.h3`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0 0 8px 0;
`;

const UploadSubtitle = styled.p`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 14px;
  color: #6d6d6d;
  margin-top: 10px;
`;

const FileList = styled.div`
  margin-top: 16px;
  width: 100%;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${props => (props.failed ? '#fff5f5' : 'white')};
  border: 1px solid ${props => (props.failed ? '#940420' : '#e0e0e0')};
  border-radius: 4px;
  margin-bottom: 8px;
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoText = styled.div`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 12px;
  color: #6d6d6d;
  text-align: center;
`;

const AdiitionalNote = styled.div`
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 14px;
  color: #6d6d6d;
  text-align: left;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const FileDetails = styled.div`
  min-width: 0;
  flex: 1;
`;

const FileName = styled.span`
  margin-left: 8px;
  color: #333;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span`
  margin-left: 8px;
  color: #6d6d6d;
  font-size: 12px;
  white-space: nowrap;
`;

const Thumbnail = styled.img`
  max-width: 40px;
  max-height: 40px;
  border: 1px solid #d7d7d7;
  padding: 2px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #940420;
  cursor: pointer;
  padding: 4px;
  font-size: 16px;

  &:hover {
    color: #c0002a;
  }
`;

const ProgressTrack = styled.div`
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin-top: 6px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.percent || 0}%;
  background: #d40f7d;
  transition: width 0.2s ease;
`;

const ErrorMessage = styled.div`
  color: #940420;
  font-size: 12px;
  font-weight: bold;
  margin-top: 8px;
  text-align: left;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FA_BY_TYPE = [
  [/^image/, 'file-image-o'],
  [/pdf/, 'file-pdf-o'],
  [/(excel|spreadsheet)/, 'file-excel-o'],
  [/wordprocessing/, 'file-word-o'],
  [/zip/, 'file-archive-o'],
];

const FA_BY_EXTENSION = [
  [/\.pdf$/, 'file-pdf-o'],
  [/\.(xls|xlsx|csv|xlsb)$/, 'file-excel-o'],
  [/\.(doc|docx)$/, 'file-word-o'],
  [/\.zip$/, 'file-archive-o'],
  [/\.(jpg|jpeg|png|gif|tiff|heic|webp)$/, 'file-image-o'],
];

const fileIconFor = (name, type) => {
  const lowerType = (type || '').toLowerCase();
  const lowerName = (name || '').toLowerCase();
  const byType = FA_BY_TYPE.find(([pattern]) => pattern.test(lowerType));
  if (byType) return byType[1];
  const byExtension = FA_BY_EXTENSION.find(([pattern]) => pattern.test(lowerName));
  return byExtension ? byExtension[1] : 'file-text-o';
};

const validateFile = (file, accept, maxFileSize) => {
  const errors = [];

  if (accept) {
    const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase());
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const mimeType = file.type.toLowerCase();

    const isAccepted = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return type === fileExtension;
      }
      return mimeType.startsWith(type.replace('*', ''));
    });

    if (!isAccepted) {
      errors.push(`File type not accepted. Accepted formats: ${accept}`);
    }
  }

  if (maxFileSize && file.size > maxFileSize) {
    errors.push(`File size exceeds limit of ${getFileSize(maxFileSize)}`);
  }

  return errors;
};

function valueToArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value) return [value];
  return [];
}

// persisted through redux, so completed uploads only — a File handle would not serialize
const persistableEntries = entries =>
  entries
    .filter(entry => entry.status === 'done')
    .map(({ key, name, size, type, url }) => ({ key, name, size, type, url, status: 'done' }));

// jsdom has no object-URL support, and a thumbnail is a nicety either way
const previewUrlFor = file => {
  if (!(file.type || '').startsWith('image/') || typeof URL.createObjectURL !== 'function') return null;
  try {
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
};

const revokePreview = url => {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
};

const entryForUrl = url => ({
  key: `value-${url}`,
  name: fileNameFromUrl(url) || 'file',
  size: null,
  type: '',
  url,
  status: 'done',
});

const DragAndDropFileWidget = props => {
  const {
    id,
    value: valueOuter,
    disabled,
    readonly,
    readOnly,
    onChange: onChangeOuter,
    options = {},
    schema = {},
    t,
    formContext = {},
    mimeTypes,
    multiple,
    maxFileSize: maxFileSizeProp,
    maxFiles: maxFilesProp,
    track = {},
    onIsUploading,
    onChangeSize,
    onFailure,
    fileUploadState,
    saveFileUploadState,
  } = props;

  const isReadOnly = disabled || readonly || readOnly;

  const accept = useMemo(() => {
    if (mimeTypes && mimeTypes.length > 0) return mimeTypes.join(',');
    return options.accept || '.pdf,.jpg,.jpeg,.png';
  }, [mimeTypes, options.accept]);

  const maxFileSize = maxFileSizeProp || options.maxFileSize || 10485760;

  const fieldType = schema.type;
  const expectsArray = multiple === undefined ? fieldType === 'array' : multiple;
  // the schema-driven default of 5 predates the merge; imperative callers came from a
  // widget with no count cap, so do not invent one for them
  const maxFiles = expectsArray
    ? maxFilesProp || options.maxFiles || schema.maxItems || (schema.type ? 5 : Infinity)
    : 1;
  const additionalNote = schema.additionalNote || '';

  const value = valueToArray(valueOuter);
  // the effect below reconciles against the URL set, not the array identity
  const valueKey = value.join(' ');

  const [isDragActive, setIsDragActive] = useState(false);
  const [errors, setErrors] = useState([]);
  const [entries, setEntries] = useState(() => {
    const restored = fileUploadState?.entries;
    if (restored?.length) return restored;
    return value.filter(Boolean).map(entryForUrl);
  });

  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const activeUploadsRef = useRef(0);
  const settledKeysRef = useRef(new Set());
  const valueRef = useRef(value);
  valueRef.current = value;
  const previousValueRef = useRef(value);
  const fileInputRef = useRef(null);

  // formContext is rebuilt every SchemaForm render, so depending on it directly would
  // re-run this effect and tear down in-flight uploads; go through a ref
  const reporterRef = useRef(null);
  reporterRef.current = pending => formContext.setFileUploadStatus?.(id, pending);
  const reportUploading = useCallback(pending => reporterRef.current?.(pending), []);

  const callbacksRef = useRef(null);
  callbacksRef.current = { onChangeOuter, onIsUploading, onChangeSize, onFailure, saveFileUploadState };

  // some callers echo onChange back into value and some do not, so entries stay the source
  // of truth and clear only when a non-empty value goes empty
  useEffect(() => {
    const { current: liveValue } = valueRef;
    const previous = previousValueRef.current;
    previousValueRef.current = liveValue;

    if (previous.length > 0 && liveValue.length === 0) {
      entriesRef.current.forEach(entry => entry.uploader?.cancel());
      activeUploadsRef.current = 0;
      reportUploading(false);
      setEntries([]);
      return;
    }

    const known = new Set(entriesRef.current.map(entry => entry.url).filter(Boolean));
    const added = liveValue.filter(url => url && !known.has(url));
    if (added.length > 0) setEntries(current => [...current, ...added.map(entryForUrl)]);
  }, [valueKey, reportUploading]);

  // unmount only — see the reporterRef note above
  useEffect(
    () => () => {
      entriesRef.current.forEach(entry => entry.uploader?.cancel());
      activeUploadsRef.current = 0;
      reporterRef.current?.(false);
    },
    []
  );

  const emitChange = useCallback(
    urls => {
      const { onChangeOuter: emit } = callbacksRef.current;
      if (expectsArray) emit(urls);
      else emit(urls.length > 0 ? urls[0] : null);
    },
    [expectsArray]
  );

  const doneKey = entries
    .filter(entry => entry.status === 'done')
    .map(entry => entry.url)
    .join(' ');

  // progress ticks replace `entries` many times a second; everything downstream only cares
  // about completed uploads, so key it on those or a 15MB upload dispatches on every tick
  useEffect(() => {
    const { onChangeSize: reportSize, saveFileUploadState: persist } = callbacksRef.current;
    const done = entriesRef.current.filter(entry => entry.status === 'done');
    reportSize?.(done.reduce((total, entry) => total + (entry.size || 0), 0));
    persist?.({ entries: persistableEntries(entriesRef.current) });
  }, [doneKey]);

  // cancel() cannot abort a presign already in flight, so a removed entry still completes;
  // keying the decrement stops that late completion releasing the gate a second time
  const settle = useCallback(
    key => {
      if (settledKeysRef.current.has(key)) return;
      settledKeysRef.current.add(key);
      activeUploadsRef.current = Math.max(0, activeUploadsRef.current - 1);
      if (activeUploadsRef.current === 0) {
        reportUploading(false);
        callbacksRef.current.onIsUploading?.(false);
      }
    },
    [reportUploading]
  );

  const startUpload = useCallback(
    file => {
      const key = `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
      const uploader = formsS3Uploader(file);

      uploader.on('progress', percent => {
        setEntries(current => current.map(entry => (entry.key === key ? { ...entry, percent } : entry)));
      });

      uploader.on('complete', result => {
        setEntries(current => {
          const next = current.map(entry =>
            entry.key === key
              ? { ...entry, status: 'done', percent: 100, url: result.signedUrl, uploader: null }
              : entry
          );
          emitChange(next.filter(entry => entry.status === 'done').map(entry => entry.url));
          return next;
        });
        settle(key);
      });

      uploader.on('error', status => {
        setEntries(current =>
          current.map(entry =>
            entry.key === key ? { ...entry, status: 'failed', error: status || 'Upload failed', uploader: null } : entry
          )
        );
        settle(key);
        callbacksRef.current.onFailure?.(file.name);
        setErrors(current => [
          ...current,
          t('DragAndDropFile.upload_failed', {
            name: file.name,
            default: `${file.name} failed to upload. Please try again.`,
          }),
        ]);
      });

      uploader.start().catch(() => {});

      return {
        key,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: previewUrlFor(file),
        percent: 0,
        status: 'uploading',
        file,
        uploader,
      };
    },
    [emitChange, settle, t]
  );

  const handleFiles = useCallback(
    files => {
      if (isReadOnly) return;

      const fileArray = Array.from(files || []);
      // dragging page text or a link still fires drop; reporting an upload for it would
      // latch the submit gate on with nothing left to settle it
      if (fileArray.length === 0) return;

      const live = entriesRef.current.filter(entry => entry.status !== 'failed');
      if (live.length + fileArray.length > maxFiles) {
        setErrors([`Cannot upload more than ${maxFiles} file${maxFiles === 1 ? '' : 's'}`]);
        return;
      }

      const newErrors = [];
      const validFiles = [];
      fileArray.forEach(file => {
        const fileErrors = validateFile(file, accept, maxFileSize);
        if (fileErrors.length > 0) {
          newErrors.push(...fileErrors);
          callbacksRef.current.onFailure?.(file.name);
        } else {
          validFiles.push(file);
        }
      });

      if (newErrors.length > 0) {
        setErrors(newErrors);
        return;
      }

      setErrors([]);
      const wasIdle = activeUploadsRef.current === 0;
      activeUploadsRef.current += validFiles.length;
      reportUploading(true);
      if (wasIdle) callbacksRef.current.onIsUploading?.(true);
      const started = validFiles.map(startUpload);
      setEntries(current => [...current, ...started]);
    },
    [isReadOnly, maxFiles, accept, maxFileSize, reportUploading, startUpload]
  );

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragActive(false);
      if (isReadOnly) return;
      handleFiles(e.dataTransfer?.files);
    },
    [handleFiles, isReadOnly]
  );

  const handleDragOver = useCallback(
    e => {
      e.preventDefault();
      if (!isReadOnly) setIsDragActive(true);
    },
    [isReadOnly]
  );

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleFileInputChange = useCallback(
    e => {
      const { files } = e.target;
      if (files && files.length > 0) handleFiles(files);
      e.target.value = '';
    },
    [handleFiles]
  );

  const handleBrowseClick = useCallback(() => {
    if (!isReadOnly) fileInputRef.current?.click();
  }, [isReadOnly]);

  const removeEntry = useCallback(
    key => {
      if (isReadOnly) return;
      const target = entriesRef.current.find(entry => entry.key === key);
      if (target?.status === 'uploading') {
        target.uploader?.cancel();
        settle(key);
      }
      revokePreview(target?.preview);

      setEntries(current => {
        const next = current.filter(entry => entry.key !== key);
        if (target?.status === 'done') {
          emitChange(next.filter(entry => entry.status === 'done').map(entry => entry.url));
        }
        return next;
      });
      setErrors([]);
    },
    [isReadOnly, emitChange, settle]
  );

  const retryEntry = useCallback(
    key => {
      const target = entriesRef.current.find(entry => entry.key === key);
      if (!target?.file) return;
      setEntries(current => current.filter(entry => entry.key !== key));
      handleFiles([target.file]);
    },
    [handleFiles]
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleBrowseClick();
      }
    },
    [handleBrowseClick]
  );

  const acceptedFormats = useMemo(() => {
    if (mimeTypes && mimeTypes.length > 0) {
      return [...new Set(mimeTypes.map(type => (mime.extension(type) || '').toUpperCase()).filter(Boolean))].join(', ');
    }
    return accept
      .split(',')
      .map(format => format.trim())
      .join(', ');
  }, [mimeTypes, accept]);

  // two DTM events on purpose: `upload` in the empty state, `add` once files are listed
  const browseTrack = entries.length > 0 ? track.add : track.upload;

  return (
    /* eslint-disable react/jsx-props-no-spreading */
    <div>
      <DropZone
        id={id}
        isDragActive={isDragActive}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleBrowseClick}
        onKeyDown={handleKeyDown}
        tabIndex={isReadOnly ? -1 : 0}
        role="button"
        aria-label="Upload files"
        className={errors.length > 0 ? 'error' : ''}
        {...dataTrackMap(track.drop || {})}
      >
        <UploadIcon>
          <Icon type="cloud-upload" />
        </UploadIcon>

        <UploadTitle>
          {t('DragAndDropFile.here_instruction', {
            count: maxFiles,
            default: expectsArray ? 'Drag and Drop your files here or' : 'Drag and Drop your file here or',
          })}{' '}
          <a
            href="#browse"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleBrowseClick();
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...dataTrackMap(browseTrack || {})}
          >
            {t('DragAndDropFile.browse', { default: 'Browse' })}
          </a>
        </UploadTitle>
        <>
          <InfoText>
            {t('DragAndDropFile.accepted_formats', {
              formats: acceptedFormats,
              default: `Accepted file formats include ${acceptedFormats}.`,
            })}
          </InfoText>
          <InfoText>
            {t('DragAndDropFile.file_size_limit', {
              count: maxFiles,
              limit: getFileSize(maxFileSize),
              default: expectsArray
                ? `Files must be ${getFileSize(maxFileSize)} or less.`
                : `File must be ${getFileSize(maxFileSize)} or less.`,
            })}
          </InfoText>
        </>
      </DropZone>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={accept}
        onChange={handleFileInputChange}
        disabled={isReadOnly}
      />

      {additionalNote && (
        <AdiitionalNote>
          <UploadSubtitle>{additionalNote}</UploadSubtitle>
        </AdiitionalNote>
      )}

      {entries.length > 0 && (
        <FileList>
          {entries.map(entry => (
            <FileItem key={entry.key} failed={entry.status === 'failed'}>
              <FileInfo>
                {entry.preview ? (
                  <Thumbnail src={entry.preview} alt="" />
                ) : (
                  <Icon type={fileIconFor(entry.name, entry.type)} />
                )}
                <FileDetails>
                  <div>
                    <FileName title={entry.name}>{entry.name}</FileName>
                    {entry.size != null && <FileSize>({getFileSize(entry.size)})</FileSize>}
                    {entry.status === 'uploading' && (
                      <FileSize>
                        {t('DragAndDropFile.uploading', { default: 'Uploading' })} {entry.percent || 0}%
                      </FileSize>
                    )}
                    {entry.status === 'failed' && <FileSize>Failed: {entry.error}</FileSize>}
                  </div>
                  {entry.status === 'uploading' && (
                    <ProgressTrack>
                      <ProgressFill percent={entry.percent} />
                    </ProgressTrack>
                  )}
                </FileDetails>
              </FileInfo>
              {entry.status === 'failed' && entry.file && (
                <IconButton type="button" onClick={() => retryEntry(entry.key)} aria-label={`Retry ${entry.name}`}>
                  <Icon type="repeat" />
                </IconButton>
              )}
              {!isReadOnly && (
                <IconButton
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    removeEntry(entry.key);
                  }}
                  aria-label={entry.status === 'uploading' ? `Cancel ${entry.name}` : `Remove ${entry.name}`}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...dataTrackMap(track.remove || {})}
                >
                  <Icon type="times" />
                </IconButton>
              )}
            </FileItem>
          ))}
        </FileList>
      )}

      {errors.length > 0 && (
        <ErrorMessage>
          {errors.map(error => (
            <div key={`error-${error.substring(0, 10)}`} role="alert">
              {error}
            </div>
          ))}
        </ErrorMessage>
      )}
    </div>
    /* eslint-enable react/jsx-props-no-spreading */
  );
};

DragAndDropFileWidget.defaultProps = {
  value: [],
  disabled: false,
  readonly: false,
  readOnly: false,
  options: {},
  schema: {},
  t: (key, opts) => opts?.default || key,
  formContext: {},
  mimeTypes: null,
  multiple: undefined,
  maxFileSize: null,
  maxFiles: null,
  track: {},
  onIsUploading: null,
  onChangeSize: null,
  onFailure: null,
  fileUploadState: null,
  saveFileUploadState: null,
};

if (process.env.NODE_ENV !== 'production') {
  const trackMap = PropTypes.objectOf(PropTypes.string);

  DragAndDropFileWidget.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    disabled: PropTypes.bool,
    readonly: PropTypes.bool,
    readOnly: PropTypes.bool,

    onChange: PropTypes.func.isRequired,
    options: PropTypes.shape({
      accept: PropTypes.string,
      maxFileSize: PropTypes.number,
      maxFiles: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    schema: PropTypes.shape({
      title: PropTypes.string,
      additionalNote: PropTypes.string,
      maxItems: PropTypes.number,
    }),
    t: PropTypes.func,
    formContext: PropTypes.shape({
      setFileUploadStatus: PropTypes.func,
    }),

    // non-RJSF callers (warranty, quoting, CSV import) drive the same widget directly
    mimeTypes: PropTypes.arrayOf(PropTypes.string),
    multiple: PropTypes.bool,
    maxFileSize: PropTypes.number,
    maxFiles: PropTypes.number,
    track: PropTypes.shape({
      upload: trackMap,
      add: trackMap,
      drop: trackMap,
      remove: trackMap,
    }),
    onIsUploading: PropTypes.func,
    onChangeSize: PropTypes.func,
    onFailure: PropTypes.func,
    fileUploadState: PropTypes.shape({ entries: PropTypes.arrayOf(PropTypes.shape({ url: PropTypes.string })) }),
    saveFileUploadState: PropTypes.func,
  };
}

export default DragAndDropFileWidget;
